import {
  AmplifyCore,
  caseUtils,
  codeBenchActions,
  gradeBenchPanelActions
} from '@codametrix/ui-common';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDetail } from '../../actions/cases.action';
import {
  CodingScreenShell,
  type ActivityFilter,
  type CodingScreenApi,
  type CodingScreenState,
  type PanelContent,
  type WorkspaceMode
} from '../../project_starburst';
import { codingScreenApiFromCase, processInstanceFromCase } from './codingScreenApi';
import { codingScreenApiFromGrading } from './gradingScreenApi';
import { ActivityPanelBody } from './panels/ActivityPanelBody';
import { CaseNotesPanelBody } from './panels/CaseNotesPanelBody';

const { getCoderNotesLookupParams } = AmplifyCore.coderNotesUtils;

/** The react-router v5 match the route forwards — only its params are read here. */
type RouteMatch = { params?: Record<string, string | undefined> };

export type CodingScreenProps = {
  mode?: WorkspaceMode;
  /**
   * Shell state for BOTH modes, injected by `ConnectedCodingScreen`'s mapState from redux
   * (coding: the fetched case; grading: `state.codeBench.activeItem`) — see the container.
   */
  state?: CodingScreenState;
  /**
   * Route props forwarded by CMx.tsx's `render={(routeProps) => ...}`. Carries the
   * params the on-mount fetch keys on: `caseId`/`serviceLine` for coding routes,
   * `id` (= sdCaseId) for the grading route.
   */
  match?: RouteMatch;
};

/**
 * Presentational coding/grading screen. Renders the widened `CodingScreenShell` from the
 * mapState-provided `state` prop (BOTH modes). Because no ancestor fetches on the
 * project_starburst route, it dispatches its own fetch on mount to POPULATE redux, branched by `mode`:
 *
 *   coding / coding-quality → `getDetail({ caseId, serviceLine })` → `state.cases.apiResponse.activeItem`
 *   grading                 → `loadCQAItemFromURL({ params: { id } })` → `state.codeBench.activeItem`
 *
 * Both thunks populate redux but return no value used here; the shell state is read back by
 * `mapStateToCodingScreenProps`, exactly as the existing grade-bench reads `codeBench.activeItem`. So
 * this component holds NO local state.
 *
 * The fetch is guarded against double-firing (StrictMode / redux-driven re-render) by a
 * last-dispatched-key ref keyed on the route id it fetched; the effect re-fires only when the relevant
 * route params actually change.
 */
export const CodingScreen: React.FC<CodingScreenProps> = ({
  mode = 'coding',
  state,
  match
}) => {
  const dispatch = useDispatch();
  // The coding case redux already fetched by getDetail (same slice mapState reads). Build the real
  // placeHold api off it for coding; grading gets `codingScreenApiFromGrading()`, an all-stub adapter
  // (its placeHold rejects -- holds are coder-only, and grading omits the Hold slot). `codingScreenApiFromCase`
  // returns undefined until the case + its ProcessInstance are available, during which HoldAction
  // shows "Holds are not available yet."
  const activeCase = useSelector(
    (s: { cases?: { apiResponse?: { activeItem?: CMxAPI.Case } } }) =>
      s?.cases?.apiResponse?.activeItem
  );
  const baseApi = useMemo(
    () =>
      mode === 'grading'
        ? codingScreenApiFromGrading()
        : codingScreenApiFromCase(activeCase, dispatch),
    [mode, activeCase, dispatch]
  );

  const [activityFilter, setActivityFilter] = useState<ActivityFilter>('all');
  const api = useMemo<CodingScreenApi | undefined>(
    () =>
      baseApi && { ...baseApi, onActivityFilterChange: setActivityFilter },
    [baseApi]
  );

  // Coding-only handles for the notes + activity fetches, memoized on the loaded case.
  const caseRun = useMemo(
    () =>
      mode !== 'grading' && activeCase?.caseRuns?.length
        ? caseUtils.getCaseRun(activeCase)
        : undefined,
    [mode, activeCase]
  );
  const processInstance = useMemo(
    () => (mode !== 'grading' ? processInstanceFromCase(activeCase) : undefined),
    [mode, activeCase]
  );
  const lookupParams = useMemo(
    () =>
      caseRun
        ? (getCoderNotesLookupParams(caseRun) as AmplifyAPI.CoderNoteInputParams)
        : undefined,
    [caseRun]
  );

  // Grading-only: the CQA activity feed keys on `sd_case_id`, which lives on the loaded CQA case
  // (`state.codeBench.activeItem`, a snake_case record — hence the narrow cast). Undefined until
  // loadCQAItemFromURL has populated it.
  const gradingSdCaseId = useSelector(
    (s: { codeBench?: { activeItem?: { sd_case_id?: string } } }) =>
      s?.codeBench?.activeItem?.sd_case_id
  );

  const lastDispatchedKey = useRef<string | null>(null);
  const params = match?.params ?? {};
  const { id, caseId, serviceLine } = params;

  useEffect(() => {
    if (mode === 'grading') {
      if (!id) {
        return;
      }
      const key = `grading:${id}`;
      if (lastDispatchedKey.current === key) {
        return;
      }
      lastDispatchedKey.current = key;
      // Populate redux (state.codeBench.activeItem); the shell state is read back by
      // mapStateToCodingScreenProps, so we do NOT unwrap the return here.
      dispatch(gradeBenchPanelActions.loadCQAItemFromURL({ params: { id } }));
      return;
    }
    if (!caseId) {
      return;
    }
    const key = `coding:${caseId}`;
    if (lastDispatchedKey.current === key) {
      return;
    }
    lastDispatchedKey.current = key;
    dispatch(getDetail({ caseId, serviceLine }));
  }, [dispatch, mode, id, caseId, serviceLine]);

  // We dispatch `fetchCoderNotes` DIRECTLY (not `navigateCoderNotes`) on purpose: `navigateCoderNotes`
  // flips `state.coderNotes.isDrawerOpen`, which the globally-mounted (CMx.tsx) legacy `CMXDrawer`/
  // `CoderNotes` would react to by popping the old drawer over this screen.
  const lastCaseFetchKey = useRef<string | null>(null);
  useEffect(() => {
    if (mode === 'grading') {
      return;
    }
    if (!processInstance && !lookupParams) {
      return;
    }
    const key = `${processInstance?.guid.uuid ?? ''}|${lookupParams?.lookupValue ?? ''}`;
    if (lastCaseFetchKey.current === key) {
      return;
    }
    lastCaseFetchKey.current = key;
    if (processInstance) {
      dispatch(codeBenchActions.fetchActivity(processInstance) as any);
    }
    if (lookupParams) {
      dispatch(codeBenchActions.fetchCoderNotes(lookupParams) as any);
    }
  }, [dispatch, mode, processInstance, lookupParams]);

  // Grading Activity feed: dispatch getGraderWorkItemActivity once the loaded CQA case exposes its
  // sd_case_id (it lands after loadCQAItemFromURL). Keyed on sd_case_id so it fires once per case.
  const lastActivityKey = useRef<string | null>(null);
  useEffect(() => {
    if (mode !== 'grading' || !gradingSdCaseId) {
      return;
    }
    if (lastActivityKey.current === gradingSdCaseId) {
      return;
    }
    lastActivityKey.current = gradingSdCaseId;
    dispatch(
      gradeBenchPanelActions.getGraderWorkItemActivity(gradingSdCaseId) as any
    );
  }, [dispatch, mode, gradingSdCaseId]);

  const panelContent = useMemo<PanelContent | undefined>(() => {
    if (mode === 'grading') {
      // Grading has no coder notes; only the Activity tab is offered, fed by the grade-bench slice.
      return {
        notes: null,
        activity: (
          <ActivityPanelBody
            mode="grading"
            sdCaseId={gradingSdCaseId}
            activityFilter={activityFilter}
          />
        )
      };
    }
    return {
      notes: lookupParams ? (
        <CaseNotesPanelBody lookupParams={lookupParams} />
      ) : null,
      activity: (
        <ActivityPanelBody
          processInstance={processInstance}
          activityFilter={activityFilter}
        />
      )
    };
  }, [mode, lookupParams, processInstance, gradingSdCaseId, activityFilter]);

  return (
    <CodingScreenShell
      mode={mode}
      state={state}
      api={api}
      panelContent={panelContent}
    />
  );
};

export { CodingScreen as default };
